import React from "react";
import { cn } from "@/lib/utils";

interface PyramidLayer {
  label: string;
  value?: number;
  color?: string;
}

interface FinancingPyramidProps {
  title?: string;
  layers: PyramidLayer[];
  animated?: boolean;
  className?: string;
}

export function FinancingPyramid({
  title = "Ihr Weg zum Eigentum",
  layers,
  animated = true,
  className,
}: FinancingPyramidProps) {
  const defaultColors = [
    "from-emerald-500 to-emerald-600",  // Bottom - Eigenmittel
    "from-amber-500 to-amber-600",       // Middle - Förderungen
    "from-primary to-primary/80",        // Top - Finanzierung
  ];

  const formatCurrency = (value?: number) => {
    if (!value) return null;
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const totalHeight = 280;
  const baseWidth = 100;

  // Reverse layers so bottom is first (widest)
  const reversedLayers = [...layers].reverse();

  return (
    <div className={cn("flex flex-col items-center", className)}>
      {title && (
        <h3 className="font-semibold text-lg mb-6 text-center">{title}</h3>
      )}

      <div className="relative" style={{ height: totalHeight, width: 300 }}>
        <svg
          viewBox="0 0 100 100"
          className="w-full h-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {reversedLayers.map((layer, index) => (
              <linearGradient
                key={`gradient-${index}`}
                id={`pyramid-gradient-${index}`}
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor={`hsl(var(--primary) / ${0.6 + index * 0.2})`} />
                <stop offset="100%" stopColor={`hsl(var(--primary) / ${0.4 + index * 0.2})`} />
              </linearGradient>
            ))}
          </defs>

          {reversedLayers.map((layer, index) => {
            const layerCount = reversedLayers.length;
            const layerHeight = 100 / layerCount;
            const y = index * layerHeight;
            
            // Calculate trapezoid widths
            const topWidthPercent = 1 - (index / layerCount);
            const bottomWidthPercent = 1 - ((index + 1) / layerCount);
            
            const topWidth = baseWidth * topWidthPercent;
            const bottomWidth = index === layerCount - 1 ? 10 : baseWidth * bottomWidthPercent;
            
            const topLeft = (100 - topWidth) / 2;
            const topRight = topLeft + topWidth;
            const bottomLeft = (100 - bottomWidth) / 2;
            const bottomRight = bottomLeft + bottomWidth;

            const points = `${topLeft},${y} ${topRight},${y} ${bottomRight},${y + layerHeight} ${bottomLeft},${y + layerHeight}`;

            return (
              <g key={layer.label}>
                <polygon
                  points={points}
                  fill={`url(#pyramid-gradient-${index})`}
                  className={cn(
                    "transition-opacity duration-500",
                    animated && "animate-in fade-in slide-in-from-bottom-2"
                  )}
                  style={animated ? { animationDelay: `${(layerCount - index) * 200}ms` } : undefined}
                />
                <line
                  x1={topLeft}
                  y1={y}
                  x2={topRight}
                  y2={y}
                  stroke="white"
                  strokeWidth="0.5"
                  strokeOpacity="0.3"
                />
              </g>
            );
          })}
        </svg>

        {/* Labels */}
        {reversedLayers.map((layer, index) => {
          const layerCount = reversedLayers.length;
          const layerHeight = totalHeight / layerCount;
          const y = index * layerHeight + layerHeight / 2;

          return (
            <div
              key={`label-${layer.label}`}
              className={cn(
                "absolute left-1/2 -translate-x-1/2 text-center",
                animated && "animate-in fade-in"
              )}
              style={{
                top: y - 10,
                animationDelay: animated ? `${(layerCount - index) * 200 + 400}ms` : undefined,
              }}
            >
              <span className="text-white font-semibold text-sm drop-shadow-md">
                {layer.label}
              </span>
              {layer.value && (
                <span className="block text-white/80 text-xs drop-shadow">
                  {formatCurrency(layer.value)}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-4 mt-6">
        {layers.map((layer, index) => (
          <div key={layer.label} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ 
                background: `hsl(var(--primary) / ${0.4 + ((layers.length - index) / layers.length) * 0.6})` 
              }}
            />
            <span className="text-sm text-muted-foreground">{layer.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
